import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { combineLatest } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from '@toro/common/shared/interfaces/app-state.interface';
import { Router } from '@angular/router';
import { OperationalLimitAction } from '@toro/common/shared/state-management/operational-limit/operational-limit.action';
import { DisposableSafelly } from '@toro/common/shared/components/disposable-safelly/disposable-safelly.component';
import { HubLoComponent, IAccount } from '@toro/common/shared/components/hub-lo/hub-lo.component';
import { mapBankAccounts } from '@toro/common/shared/helpers/banks/bank-account.util';
import { DialogsService } from '@toro/common/shared/services/dialogs.service';
import { filter, map } from 'rxjs/operators';
import { BanksAction } from '@toro/common/shared/state-management/banks/banks.action';
import { TrackingActions } from '@toro/common/shared/state-management/tracking/tracking.action';
import { ActionEventsEnum } from '@toro/common/shared/enum/tracking/action-events.enum';
import { SantanderSuitabilityService } from '@toro/common/shared/services/santander-suitability/santander-suitability-service';
import { isCnpj } from '@toro/common/shared/helpers/utils/document';

@Component({
    selector: 'lo-santander',
    templateUrl: './lo-santander.component.html',
    styleUrls: ['./lo-santander.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class LoSantanderComponent extends DisposableSafelly implements OnInit {
    @Input() homeStatus: 'open' | 'active';
    public isAcceptedTerm: boolean = true;
    private documentNumber: string;
    private santanderAccounts: IAccount[] = [];
    private readonly santanderBankId = 2;
    private readonly openCardTitle = 'Conecte sua conta Santander';
    private readonly activeCardTitle = 'Autorize a comunicação entre contas';
    private bannerTracked = false;

    /**
     * Indica se a conta logada e Pessoa Juridica (documento com mais de 11 digitos = CNPJ)
     */
    public get isPj(): boolean {
        return isCnpj(this.documentNumber);
    }

    constructor(
        private store: Store<AppState>,
        private router: Router,
        private dialogsService: DialogsService,
        private santanderSuitabilityService: SantanderSuitabilityService,
    ) {
        super();
    }

    public bindDispatchs() {
        this.store.dispatch({ type: OperationalLimitAction.getAcceptanceTerm });
        this.store.dispatch({ type: BanksAction.getItems });
    }

    public ngOnInit() {
        this.bindDispatchs();
        this.disposableSubscriptions.push(
            this.store.select((state) => state.userProfile?.user?.cpfcnpj)
                .pipe(filter((cpfcnpj): cpfcnpj is string => !!cpfcnpj))
                .subscribe((cpfcnpj) => {
                    this.documentNumber = cpfcnpj;
                    this.maybeTrackBannerViewed();
                }),
        );
        this.disposableSubscriptions.push(
            combineLatest([
                this.store.select((state) => state.operationalLimit).pipe(
                    filter((op) => !op.pending),
                    map((op) => op.value?.isAccepted === true),
                ),
                this.store.select((state) => state.userProfile?.user?.bankAccounts).pipe(
                    filter((bankAccounts): bankAccounts is any[] => Array.isArray(bankAccounts)),
                    map((bankAccounts) => mapBankAccounts(bankAccounts)),
                ),
            ]).subscribe(([isAccepted, accounts]) => {
                this.isAcceptedTerm = isAccepted;
                this.santanderAccounts = accounts.filter(
                    (account) => account.bankId === this.santanderBankId,
                );
                this.maybeTrackBannerViewed();
            })
        );
    }

    /**
     * Metrica: banner de LO exibido na Home. Disparado uma unica vez, apenas
     * quando o banner realmente aparece (!isAcceptedTerm && !isPj) e ja temos o
     * documento carregado (para avaliar PJ com seguranca).
     */
    private maybeTrackBannerViewed(): void {
        if (this.bannerTracked || !this.homeStatus || !this.documentNumber) {
            return;
        }
        if (this.isAcceptedTerm || this.isPj) {
            return;
        }
        this.bannerTracked = true;
        this.sendTracking(ActionEventsEnum.ELEMENT_LOADED, {
            'Product Area Origin': 'Home',
            Element: this.homeStatus === 'open' ? 'Banner Conectar conta Santander' : 'Banner Autorizar comunicação',
            Group: 'Conexão entre contas',
            Label: this.homeStatus === 'open' ? this.openCardTitle : this.activeCardTitle,
            'Time Responsável': 'Sky',
            'Specific Properties': {
                home_type: this.homeStatus === 'open' ? 'Aberta' : 'Ativa',
                user_type: this.santanderSuitabilityService.getClientSegment(),
            },
        });
    }

    public authorizeClick() {
        let Element: string, Label: string;
        if (this.homeStatus === 'open') {
            Element = 'Botão Cadastrar conta corrente',
            Label = this.openCardTitle;
        } else {
            Element = 'Botão Autorizar';
            Label = this.activeCardTitle;
        }
        this.sendTracking(ActionEventsEnum.ELEMENT_CLICKED, {
            'Product Area Origin': 'Home',
            Element,
            Group: 'Conexão entre contas',
            Label,
            'Time Responsável': 'Sky',
            'Specific Properties': {
                home_type: this.homeStatus === 'open' ? 'Aberta' : 'Ativa',
                user_type: this.santanderSuitabilityService.getClientSegment(),
            },
          });

        if (this.santanderAccounts.length) {
            this.dialogsService.showComponentAsAsidePanel(
                HubLoComponent,
                true,
                { intent: 'authorize', accounts: this.santanderAccounts },
            );
            return;
        }
        this.router.navigateByUrl('minha-conta/dados-bancarios/edit?type=lo');
    }

    public sendTracking(event: string, breakdowns: any) {
        this.store.dispatch({
            type: TrackingActions.trackEvents,
            payload: { type: event, events: breakdowns },
        });
    }
}
